import {Injectable} from '@angular/core';
import {Theme} from '../interfaces/theme';
import {themeDefinitions} from '../definitions/theme-definitions';
import {TranslateService} from '@ngx-translate/core';
import {ContractorService} from './contractor.service';
import {ThemeDefinition} from '../interfaces/theme-definition';
import {StorageService} from '@twict4fiber/storage';
import {ModalService} from './ionic/modal.service';
import {ThemeSelectorComponent} from '../components/theme-selector/theme-selector.component';
import {SK_1_SETTINGS_PREFERENCES_THEME} from '../constants/storage-keys';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {

  private _preferredThemeId: string;

  private _selectedTheme: Theme;

  public get selectedTheme(): Theme {
    return this._selectedTheme;
  }

  public get preferredThemeId(): string {
    return this._preferredThemeId;
  }

  public constructor(private contractorService: ContractorService,
                     private translateService: TranslateService,
                     private storageService: StorageService,
                     private modalService: ModalService) {
  }

  public async initialize(): Promise<void> {
    const preferredThemeId: any = await this.storageService.getItem(SK_1_SETTINGS_PREFERENCES_THEME);

    try {
      if (typeof preferredThemeId === 'string') {
        this._preferredThemeId = preferredThemeId;
      }
    } catch (err: any) {
      console.warn(err);
    }

    const preferredTheme: Theme = this.getPreferredTheme(this._preferredThemeId);
    this.activateTheme(preferredTheme);
  }

  public getPreferredTheme(preferredThemeId: string): Theme {
    const availableThemes: ThemeDefinition[] = this.getAvailableThemes();

    let preferredTheme: Theme = availableThemes?.find((availableThemes: ThemeDefinition): boolean => {
      return preferredThemeId && preferredThemeId?.trim()?.toLowerCase() === availableThemes?.id?.trim()?.toLowerCase();
    });

    if (!preferredTheme) {
      if (availableThemes?.length > 0) {
        const preferDark: boolean = this.deviceUsesDarkTheme();

        preferredTheme = availableThemes.find((availableTheme: ThemeDefinition): boolean => {
          return availableTheme && (availableTheme.dark === undefined || availableTheme.dark === null || availableTheme.dark === preferDark);
        });

        if (!preferredTheme) {
          preferredTheme = availableThemes[0];
        }
      } else {
        preferredTheme = {id: null, name: this.translateService.instant('Automatic'), class: null};
      }
    }

    return preferredTheme;
  }

  public activateTheme(theme: Theme): void {
    for (const themeDefinition of themeDefinitions) {
      document.body.classList.remove(themeDefinition.class);
    }

    if (theme?.class) {
      document.body.classList.add(theme.class);
    }

    this._selectedTheme = theme;
  }

  public getAvailableThemes(): ThemeDefinition[] {
    const contractorId: string = this.contractorService?.selectedContractorOld?.id?.trim()?.toLowerCase();

    return themeDefinitions?.filter((themeDefinition: ThemeDefinition): boolean => {
      return themeDefinition && (!themeDefinition.includedContractorIds || themeDefinition.includedContractorIds.some((includedContractorId: string): boolean => {
        return contractorId && includedContractorId && contractorId === includedContractorId.trim().toLowerCase();
      })) && (!themeDefinition.excludedContractorIds || !themeDefinition.excludedContractorIds.some((excludedContractorId: string): boolean => {
        return contractorId && excludedContractorId && contractorId === excludedContractorId.trim().toLowerCase();
      }))
    });
  }

  public async showThemeSelection(): Promise<void> {
    await this.modalService.presentModal({
      component: ThemeSelectorComponent
    }, true);
  }

  public async setPreferredThemeId(preferredThemeId: string): Promise<void> {
    this._preferredThemeId = preferredThemeId;

    if (this.preferredThemeId) {
      await this.storageService.setItem(SK_1_SETTINGS_PREFERENCES_THEME, this.preferredThemeId);
    } else {
      await this.storageService.removeItem(SK_1_SETTINGS_PREFERENCES_THEME);
    }
  }

  private deviceUsesDarkTheme(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

}
